<template>
  <div class="app-container">
    <MobileNavigation
      v-if="showNavigation"
      :supervisor-current-tab="supervisorTab"
      @update:supervisorTab="updateSupervisorTab"
    />
    <router-view
      :supervisor-tab="supervisorTab"
      @update:supervisorTab="updateSupervisorTab"
    />
  </div>
</template>

<script>
import MobileNavigation from './components/MobileNavigation.vue';

export default {
  name: 'App',
  components: {
    MobileNavigation
  },
  data() {
    return {
      supervisorTab: 'traffic' // Default tab
    };
  },
  computed: {
    showNavigation() {
      // Don't show navigation on login page
      return this.$route.name !== 'login';
    }
  },
  mounted() {
    // Initialize theme on app load
    this.initializeTheme();
  },
  methods: {
    updateSupervisorTab(tab) {
      this.supervisorTab = tab;
    },
    initializeTheme() {
      // Check for saved theme preference
      const savedTheme = localStorage.getItem('autooperator-theme');

      if (savedTheme) {
        document.documentElement.setAttribute('data-theme', savedTheme);
      } else {
        // Fall back to system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
      }

      // Update meta theme-color
      this.updateMetaThemeColor();
    },
    updateMetaThemeColor() {
      const theme = document.documentElement.getAttribute('data-theme');
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute('content', theme === 'dark' ? '#1e1e1e' : '#ffffff');
      }
    }
  }
};
</script>

<style>
/* Import design systems */
@import '@/styles/design-system.css';
@import '@/assets/mobile-design-system.css';

/*
 * Legacy CSS Variables (for backward compatibility during migration)
 * These map old variable names to the new design system
 * Remove these after all components are migrated
 */
:root {
  /* Legacy color mappings */
  --primary-color: var(--color-primary);
  --primary-light: var(--color-primary-light);
  --primary-dark: var(--color-primary-dark);
  --secondary-color: var(--color-primary-light);
  --secondary-light: #80cbc4;
  --secondary-dark: var(--color-primary-dark);
  --error-color: var(--color-error);
  --success-color: var(--color-success);
  --warning-color: var(--color-warning);
}

[data-theme="light"] {
  --background-dark: var(--bg-secondary);
  --surface-dark: var(--bg-primary);
  --card-dark: var(--card-bg);
  --text-primary: var(--text-primary);
  --text-secondary: var(--text-secondary);
  --border-color: var(--border-default);
  --hover-color: var(--bg-hover);
}

[data-theme="dark"] {
  --background-dark: #121212;
  --surface-dark: #1e1e1e;
  --card-dark: #2d2d2d;
  --text-primary: #ffffff;
  --text-secondary: #b0b0b0;
  --border-color: #333333;
  --hover-color: #3a3a3a;
}

/* Base HTML/Body styles */
html,
body {
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-family: var(--font-family);
  font-size: 16px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-container {
  min-height: 100vh;
  background-color: var(--bg-secondary);
}

/* Global link styling */
a {
  color: var(--text-link);
  text-decoration: none;
  transition: color var(--transition-fast);
}

a:hover {
  color: var(--text-link-hover);
  text-decoration: underline;
}

/* Global button styling (base - can be overridden by .btn classes) */
button {
  cursor: pointer;
  border: none;
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-4);
  font-family: var(--font-family);
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-fast);
  background-color: var(--bg-tertiary);
  color: var(--text-primary);
}

button:hover:not(:disabled) {
  background-color: var(--bg-hover);
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Global input styling (base - can be overridden by .form-* classes) */
input, select, textarea {
  background-color: var(--input-bg);
  border: 1px solid var(--input-border);
  color: var(--text-primary);
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-3);
  font-family: var(--font-family);
  font-size: var(--font-size-base);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

input:focus, select:focus, textarea:focus {
  outline: none;
  border-color: var(--input-focus-border);
  box-shadow: var(--input-focus-shadow);
}

input::placeholder, textarea::placeholder {
  color: var(--input-placeholder);
}

/* Global table styling (base - can be overridden by .data-table classes) */
table {
  width: 100%;
  border-collapse: collapse;
  background-color: var(--card-bg);
  border: 1px solid var(--table-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

th, td {
  border-bottom: 1px solid var(--table-border);
  padding: var(--space-3) var(--space-4);
  text-align: left;
  color: var(--text-primary);
}

th {
  background-color: var(--table-header-bg);
  color: var(--text-secondary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
}

tbody tr:hover {
  background-color: var(--table-row-hover);
}

tbody tr:last-child td {
  border-bottom: none;
}

/* Selection styling */
::selection {
  background-color: rgba(var(--color-primary-rgb), 0.2);
  color: var(--text-primary);
}

/* Focus visible for keyboard navigation */
:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Disable outline for mouse users */
:focus:not(:focus-visible) {
  outline: none;
}
</style>
